import {
  IconButton,
  InputAdornment,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
} from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import { RootStateOrAny, useSelector } from 'react-redux';
import ITranslation from '../../../types/ITranslation';
import styles from '../styles';
import { REGEX_MULTILINGUAL } from '../../../utils/regex_multilingual';
import CloseIcon from '@material-ui/icons/Close';
import { KeyedElement } from '../../AttributesDialog/hooks/useCollection';

interface ITranslationRow {
  elements: KeyedElement<ITranslation>[];
  element: KeyedElement<ITranslation>;
  index: number;
  editTranslation: (props: {
    index: number;
    field: string;
    value: any;
    regex?: any;
    sizeLimit?: number;
  }) => void;
  key?: string;
  removeTranslation: (index: number) => void;
  disabled?: boolean;
}

export default function TranslationRow({
  elements,
  element,
  index,
  editTranslation,
  removeTranslation,
  disabled,
}: ITranslationRow) {
  const { languages } = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer,
  );

  const [touched, setTouched] = useState(false);
  const [hasFocus, setHasFocus] = useState(false);

  const selectorErrorRef = useRef('');
  const [selectorErrorText, setSelectorErrorText] = useState('');

  const textFieldErrorRef = useRef('');
  const [textFieldErrorText, setTextFieldErrorText] = useState('');

  const languageCode = element?.language?.code;
  const textFieldContents = element?.text;

  useEffect(() => {
    if (hasFocus || disabled || !touched) return;
    setSelectorErrorText(selectorErrorRef.current);
    setTextFieldErrorText(textFieldErrorRef.current);
  }, [hasFocus, disabled, touched]);

  useEffect(() => {
    const newSelectErrorText = languageCode ? '' : 'No language selected';
    selectorErrorRef.current = newSelectErrorText;
  }, [languageCode]);

  useEffect(() => {
    const newTextFieldErrorText = textFieldContents?.trim() ? '' : 'Item cannot be blank'
    textFieldErrorRef.current = newTextFieldErrorText;
  }, [textFieldContents]);

  const otherActiveLanguageIds = new Set(elements.filter((e, i) => i !== index).map(e => e.language.id));

  // This gives both inputs the same vertical alignment when exactly one of them has an error
  const selectorHelperText = selectorErrorText || (textFieldErrorText ? ' ' : '');
  const textFieldHelperText = textFieldErrorText || (selectorErrorText ? ' ' : '');

  const langList = Object.keys(languages)
    .filter(val => val !== 'headers')
    .map(val => languages[val])
    .filter(val => !otherActiveLanguageIds.has(val.id));

  const classes = styles();

  const handleBlur = () => {
    setHasFocus(false);
    setTouched(true);
  }

  return (
    <TableRow key={element.key}>
      <TableCell className={classes.tableCell}>
        <TextField
          disabled={disabled}
          select
          error={!!selectorErrorText}
          helperText={selectorHelperText}
          className={classes.tableField}
          variant="outlined"
          size="small"
          fullWidth
          label="Languages"
          value={languageCode}
          onChange={event => {
            editTranslation({
              index,
              field: 'language',
              value: langList.find(value => value.code === event.target.value),
            });
          }}
        >
          {langList.map(value => {
            return (
              <MenuItem value={value.code} key={value.id}>
                {value.name}
              </MenuItem>
            );
          })}
        </TextField>
      </TableCell>
      <TableCell className={classes.tableCell} colSpan={2}>
        <TextField
          autoFocus={!textFieldContents}
          error={!!textFieldErrorText}
          helperText={textFieldHelperText}
          disabled={disabled}
          size="small"
          variant="outlined"
          placeholder="Enter Translations"
          className={classes.tableField}
          fullWidth
          value={textFieldContents}
          autoComplete="off"
          onFocus={() => setHasFocus(true)}
          onBlur={handleBlur}
          InputProps={{
            endAdornment: !disabled && (
              <InputAdornment position="end">
                <IconButton
                  disabled={disabled}
                  aria-label="close"
                  onClick={() => removeTranslation(index)}
                >
                  <CloseIcon opacity="0.5" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          onChange={event => {
            editTranslation({
              index,
              field: 'text',
              value: event.target.value as string,
              regex: REGEX_MULTILINGUAL,
              sizeLimit: 30,
            });
          }}
        />
      </TableCell>
    </TableRow>
  );
}
